import React from 'react';

/**
 * Link rendering helpers for recruiter/candidate authored plain text.
 *
 * Output is an array of React nodes (plain strings interleaved with <a>
 * elements) instead of injected HTML, so there is no dangerouslySetInnerHTML
 * and therefore no HTML/XSS surface. Only http(s) hrefs are ever emitted.
 */

// [label](url), or a bare http(s):// / www. URL
const LINK_PATTERN = /\[([^\]\n]+)\]\(\s*([^\s)]+)\s*\)|((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;

// Punctuation that commonly trails a URL in prose and must stay out of the href
const TRAILING_CHARS = '.,;:!?*_~\'"“”‘’]}>';

/**
 * Strips sentence punctuation from the end of a bare URL, while keeping a
 * closing paren that belongs to the URL itself (e.g. "..._(disambiguation)").
 * @param {string} url - Raw matched URL
 * @returns {string} - URL without trailing punctuation
 */
const trimTrailingPunctuation = (url) => {
    let end = url.length;

    while (end > 0) {
        const char = url[end - 1];

        if (char === ')') {
            const candidate = url.slice(0, end);
            const opened = (candidate.match(/\(/g) || []).length;
            const closed = (candidate.match(/\)/g) || []).length;
            if (opened >= closed) break;
            end -= 1;
            continue;
        }

        if (TRAILING_CHARS.indexOf(char) === -1) break;
        end -= 1;
    }

    return url.slice(0, end);
};

/**
 * Normalises a candidate URL to an absolute http(s) href. Anything else
 * (javascript:, data:, vbscript:, protocol-relative, relative paths) is
 * rejected so it can be rendered as ordinary text.
 * @param {string} url - Candidate URL
 * @returns {string|null} - Safe href, or null when the scheme is not allowed
 */
const toSafeHref = (url) => {
    const value = (url || '').trim();
    if (!value) return null;

    const candidate = /^www\./i.test(value) ? `https://${value}` : value;
    return /^https?:\/\/[^\s]+$/i.test(candidate) ? candidate : null;
};

/**
 * Renders text with clickable links, preserving everything else verbatim.
 * @param {string} text - Text to render
 * @param {*} fallback - Rendered instead when text is empty
 * @returns {*} - The fallback, the original string, or an array of React nodes
 */
export const renderTextWithLinks = (text, fallback = '') => {
    if (text === null || text === undefined || text === '') return fallback;

    const source = typeof text === 'string' ? text : String(text);
    const nodes = [];
    let cursor = 0;
    let match;

    LINK_PATTERN.lastIndex = 0;

    while ((match = LINK_PATTERN.exec(source)) !== null) {
        const [matched, mdLabel, mdUrl, bareUrl] = match;
        const isMarkdown = mdUrl !== undefined;
        const url = isMarkdown ? mdUrl : trimTrailingPunctuation(bareUrl);
        const href = toSafeHref(url);

        if (match.index > cursor) {
            nodes.push(source.slice(cursor, match.index));
        }

        if (href) {
            nodes.push(
                <a
                    key={`link-${match.index}`}
                    href={href}
                    className="markdown-link"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {isMarkdown ? mdLabel : url}
                </a>
            );

            // Punctuation trimmed off a bare URL stays as ordinary text
            const tail = isMarkdown ? '' : bareUrl.slice(url.length);
            if (tail) nodes.push(tail);
        } else {
            nodes.push(matched);
        }

        cursor = match.index + matched.length;
    }

    if (nodes.length === 0) return source;

    if (cursor < source.length) {
        nodes.push(source.slice(cursor));
    }

    return nodes;
};
